import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CalendarDays, Check, Loader2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  MAX_CYCLE_DAYS,
  WEEKDAY_LETTERS,
  cycleDayLabel,
  cycleWeeks,
  formatHours,
  patternSummary,
  resizeCycle,
  shiftFitsDay,
  weekdaysLabel,
} from '@/lib/shiftHours'
import {
  resourceCenterApi,
  type ShiftDefinition,
  type ShiftPattern,
} from '@/api/resourceCenterApi'
import {
  useInvalidateResourceCenter,
  useShiftDefinitions,
  useShiftPatterns,
} from '@/hooks/useResourceCenter'
import { SHIFT_TONES, shiftTone } from './shiftTones'

interface DayDraft {
  isOn: boolean
  /** The shift worked that day (z8nrz7f7g8); null on an OFF day, or an ON day still to pick. */
  shiftId: string | null
}

interface Draft {
  name: string
  description: string
  days: DayDraft[]
}

const blankDay = (): DayDraft => ({ isOn: false, shiftId: null })

const NEW_DRAFT = (): Draft => ({
  name: '',
  description: '',
  days: Array.from({ length: 7 }, blankDay),
})

const toDraft = (p: ShiftPattern): Draft => ({
  name: p.name,
  description: p.description ?? '',
  days: p.days.map(d => ({ isOn: d.isOn, shiftId: d.isOn ? (d.shift?.id ?? null) : null })),
})

/** "2 weeks", "8 days". */
function cycleLabel(days: number): string {
  if (days % 7 === 0) return `${days / 7} ${days === 7 ? 'week' : 'weeks'}`
  return `${days} days`
}

/**
 * Center Setup → Pattern library (z8nrz7f0vu): reusable rotations, counted in
 * weeks from Sunday. A resource that starts a pattern on 10/1 is anchored to
 * the Sunday of that week (9/27), so 10/1 falls in week A. Tap a day to switch
 * it ON or OFF.
 */
export function ShiftPatternsDialog({
  open,
  onOpenChange,
  canManage,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  canManage: boolean
}) {
  const patterns = useShiftPatterns()
  const shifts = useShiftDefinitions()
  // null = nothing selected; 'new' = drafting a new pattern.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const selected = patterns.data?.find(p => p.id === selectedId) ?? null

  // Land on the first pattern rather than an empty editor.
  useEffect(() => {
    if (open && selectedId === null && patterns.data?.length) setSelectedId(patterns.data[0].id)
  }, [open, selectedId, patterns.data])

  const firstActiveShift = shifts.data?.find(s => s.active)

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        setError(null)
        onOpenChange(o)
      }}
    >
      <DialogContent
        className='max-h-[90vh] overflow-y-auto sm:max-w-5xl'
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Pattern library</DialogTitle>
          <DialogDescription>
            Reusable rotations, counted in weeks from Sunday. A resource's cycle starts on the
            Sunday of the week they begin the pattern.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 md:grid-cols-[17rem_1fr]'>
          <div className='space-y-2'>
            {canManage && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='w-full'
                disabled={!firstActiveShift}
                title={firstActiveShift ? undefined : 'Add a shift definition first'}
                onClick={() => {
                  setSelectedId('new')
                  setError(null)
                }}
              >
                <Plus className='mr-1.5 h-3.5 w-3.5' /> New pattern
              </Button>
            )}
            {patterns.isLoading && <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />}
            {patterns.data?.length === 0 && (
              <p className='text-xs text-muted-foreground'>No patterns yet.</p>
            )}
            <ul className='space-y-1'>
              {patterns.data?.map(p => (
                <li key={p.id}>
                  <button
                    type='button'
                    onClick={() => {
                      setSelectedId(p.id)
                      setError(null)
                    }}
                    className={cn(
                      'w-full space-y-1 rounded-md border px-2.5 py-2 text-left hover:bg-accent',
                      p.id === selectedId && 'border-primary bg-accent',
                      !p.active && 'opacity-60',
                    )}
                  >
                    <span className='flex items-center justify-between gap-2 text-sm font-medium'>
                      <span className='truncate'>{p.name}</span>
                      <span className='shrink-0 font-mono text-xs text-muted-foreground'>
                        {p.shifts.map(sh => sh.code).join(' · ')}
                      </span>
                    </span>
                    <DaysStrip days={p.days.map(d => d.isOn)} />
                    <span className='block text-[11px] text-muted-foreground'>
                      {cycleLabel(p.cycleDays)} · {formatHours(p.averageWeeklyHours)}/week
                      {!p.active && ' · inactive'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className='space-y-3'>
            {error && (
              <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>
            )}
            {selectedId === 'new' && firstActiveShift ? (
              <PatternEditor
                key='new'
                shifts={shifts.data ?? []}
                shiftsLoading={shifts.isLoading}
                initial={NEW_DRAFT()}
                canManage={canManage}
                onSaved={p => {
                  setSelectedId(p.id)
                  setError(null)
                }}
                onError={setError}
              />
            ) : selected ? (
              <PatternEditor
                key={selected.id}
                pattern={selected}
                shifts={shifts.data ?? []}
                shiftsLoading={shifts.isLoading}
                initial={toDraft(selected)}
                canManage={canManage}
                onSaved={() => setError(null)}
                onDeleted={() => {
                  setSelectedId(null)
                  setError(null)
                }}
                onError={setError}
              />
            ) : (
              <p className='text-sm text-muted-foreground'>Select a pattern to see its cycle.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** The compact ON/OFF strip shown in the library list. */
function DaysStrip({ days }: { days: boolean[] }) {
  return (
    <span className='flex flex-wrap gap-0.5' aria-hidden>
      {days.map((on, i) => (
        <span
          key={i}
          className={cn('h-2 w-2 rounded-[2px]', on ? 'bg-primary' : 'bg-muted-foreground/20')}
        />
      ))}
    </span>
  )
}

function PatternEditor({
  pattern,
  shifts,
  shiftsLoading,
  initial,
  canManage,
  onSaved,
  onDeleted,
  onError,
}: {
  pattern?: ShiftPattern
  shifts: ShiftDefinition[]
  shiftsLoading: boolean
  initial: Draft
  canManage: boolean
  onSaved: (p: ShiftPattern) => void
  onDeleted?: () => void
  onError: (message: string | null) => void
}) {
  const invalidate = useInvalidateResourceCenter()
  const [draft, setDraft] = useState<Draft>(initial)

  // Adopt the server's copy after a save lands (or a shift edit elsewhere).
  // Keyed on updatedAt, not identity: an unrelated refetch must not wipe unsaved picks.
  const serverVersion = pattern?.updatedAt
  useEffect(() => {
    if (pattern) setDraft(toDraft(pattern))
  }, [serverVersion])

  const shiftById = useMemo(() => new Map(shifts.map(s => [s.id, s])), [shifts])
  const summary = patternSummary(
    draft.days.map(d => ({
      isOn: d.isOn,
      hours: (d.shiftId ? shiftById.get(d.shiftId)?.hours : undefined) ?? 0,
    })),
  )
  const cycleDays = draft.days.length
  const toneOf = (id: string) => shiftTone(shifts.map(sh => sh.id), id)
  // An ON day with no shift, or one whose shift no longer fits (e.g. after the
  // cycle was resized so the day now drifts across the week).
  const fitsDay = (d: DayDraft, i: number) => {
    const sh = d.shiftId ? shiftById.get(d.shiftId) : undefined
    return !!sh && shiftFitsDay(sh.weekdays, i, cycleDays)
  }
  const unpicked = draft.days.findIndex((d, i) => d.isOn && !fitsDay(d, i))
  // The day whose Component modal is open.
  const [choosing, setChoosing] = useState<number | null>(null)

  /** Set the days to a component (or OFF with null). */
  const setDays = (indexes: number[], shiftId: string | null) =>
    setDraft(d => ({
      ...d,
      days: d.days.map((x, i) => (indexes.includes(i) ? (shiftId ? { isOn: true, shiftId } : blankDay()) : x)),
    }))

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial) || !pattern
  // Until the shift list loads, days cannot be matched to shifts.
  const readOnly = !canManage || shiftsLoading
  const fail = (e: unknown) => onError(e instanceof Error ? e.message : 'Something went wrong.')

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        days: draft.days.map(d => ({ isOn: d.isOn, shiftId: d.isOn ? d.shiftId : null })),
      }
      return pattern
        ? resourceCenterApi.updatePattern(pattern.id, body)
        : resourceCenterApi.createPattern({ ...body, description: body.description ?? undefined })
    },
    onSuccess: p => {
      void invalidate()
      onSaved(p)
    },
    onError: fail,
  })
  const toggle = useMutation({
    mutationFn: (active: boolean) => resourceCenterApi.updatePattern(pattern!.id, { active }),
    onSuccess: p => {
      void invalidate()
      onSaved(p)
    },
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: () => resourceCenterApi.removePattern(pattern!.id),
    onSuccess: () => {
      void invalidate()
      onDeleted?.()
    },
    onError: fail,
  })

  const setCycle = (length: number) =>
    setDraft(d => ({ ...d, days: resizeCycle(d.days, length, blankDay) }))

  // Applied on blur / Enter, not per keystroke: clearing the box or passing through
  // "2" on the way to "21" would otherwise cut the cycle and drop its day settings.
  const cycleLength = draft.days.length
  const [cycleText, setCycleText] = useState(String(cycleLength))
  useEffect(() => setCycleText(String(cycleLength)), [cycleLength])
  const commitCycleText = () => {
    const n = Number(cycleText)
    if (cycleText.trim() !== '' && Number.isInteger(n) && n >= 1) {
      setCycle(n)
      setCycleText(String(Math.min(n, MAX_CYCLE_DAYS)))
    } else setCycleText(String(cycleLength))
  }

  return (
    <div className='space-y-4 rounded-lg border p-4'>
      <div className='grid gap-3 sm:grid-cols-2'>
        <div className='space-y-1.5'>
          <Label htmlFor='pattern-name' className='text-xs'>
            Name
          </Label>
          <Input
            id='pattern-name'
            value={draft.name}
            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            placeholder='e.g. 2-week 3 on / 4 off'
            readOnly={readOnly}
            className='h-8 text-sm'
          />
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='pattern-cycle' className='text-xs'>
            Cycle
          </Label>
          <div className='flex items-center gap-2'>
            <div className='flex rounded-md bg-muted p-0.5 text-xs' role='radiogroup' aria-label='Cycle in weeks'>
              {[1, 2, 3, 4].map(w => (
                <button
                  key={w}
                  type='button'
                  role='radio'
                  aria-checked={draft.days.length === w * 7}
                  disabled={readOnly}
                  onClick={() => setCycle(w * 7)}
                  className={cn(
                    'rounded px-2.5 py-1 font-medium transition-colors',
                    draft.days.length === w * 7
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {w} wk
                </button>
              ))}
            </div>
            <div className='relative'>
              <Input
                id='pattern-cycle'
                type='number'
                min={1}
                max={MAX_CYCLE_DAYS}
                value={cycleText}
                onChange={e => setCycleText(e.target.value)}
                onBlur={commitCycleText}
                onKeyDown={e => e.key === 'Enter' && commitCycleText()}
                readOnly={readOnly}
                aria-label='Cycle length in days'
                className='h-8 w-24 pr-10 text-sm'
              />
              <span className='pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground'>
                days
              </span>
            </div>
          </div>
        </div>
        <div className='space-y-1.5'>
          <Label htmlFor='pattern-description' className='text-xs'>
            Description <span className='font-normal text-muted-foreground'>(optional)</span>
          </Label>
          <Input
            id='pattern-description'
            value={draft.description}
            onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            readOnly={readOnly}
            className='h-8 text-sm'
          />
        </div>
      </div>

      <div className='space-y-3'>
        <div className='space-y-1.5'>
          <div className='grid grid-cols-[4.5rem_repeat(7,minmax(0,1fr))] gap-1.5 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
            <span />
            {WEEKDAY_LETTERS.map(d => (
              <span key={d}>{d}</span>
            ))}
          </div>
          {cycleWeeks(draft.days).map(week => (
            <div key={week.label} className='grid grid-cols-[4.5rem_repeat(7,minmax(0,1fr))] gap-1.5'>
              <span className='self-center text-xs font-medium text-muted-foreground'>{week.label}</span>
              {week.days.map(({ index, day }) => (
                <DayCell
                  key={index}
                  index={index}
                  day={day}
                  shift={day.shiftId ? shiftById.get(day.shiftId) : undefined}
                  tone={day.shiftId ? toneOf(day.shiftId).cell : undefined}
                  misfit={day.isOn && !fitsDay(day, index)}
                  readOnly={readOnly}
                  onChoose={() => setChoosing(index)}
                />
              ))}
            </div>
          ))}
        </div>
        <ComponentLegend days={draft.days} shiftById={shiftById} toneOf={toneOf} />
        {choosing !== null && (
          <ComponentDialog
            dayIndex={choosing}
            cycleDays={cycleDays}
            current={draft.days[choosing]}
            // Active shifts, plus the day's own one even if it was since retired.
            shifts={shifts.filter(sh => sh.active || sh.id === draft.days[choosing].shiftId)}
            toneOf={toneOf}
            onClose={() => setChoosing(null)}
            onChoose={(shiftId, everyWeek) => {
              const indexes = everyWeek
                ? draft.days.map((_, i) => i).filter(i => i % 7 === choosing % 7)
                : [choosing]
              // A component only goes on days it may be worked.
              const sh = shiftId ? shiftById.get(shiftId) : undefined
              setDays(sh ? indexes.filter(i => shiftFitsDay(sh.weekdays, i, cycleDays)) : indexes, shiftId)
              setChoosing(null)
            }}
          />
        )}
        <div className='flex flex-wrap items-center gap-x-5 gap-y-1 pt-2 text-sm text-muted-foreground'>
          <span>
            <span className='font-semibold text-foreground tabular-nums'>{summary.workingDays}</span> working{' '}
            {summary.workingDays === 1 ? 'day' : 'days'} in a {draft.days.length}-day cycle
          </span>
          <span>
            <span className='font-semibold text-foreground tabular-nums'>
              {formatHours(summary.averageWeeklyHours)}
            </span>{' '}
            per week on average
          </span>
          {!readOnly && (
            <span className='text-xs'>Tap a day to choose its component.</span>
          )}
        </div>
      </div>

      <div className='flex items-center justify-between gap-2 border-t pt-3'>
        <div className='flex items-center gap-3'>
          {pattern && (
            <label className='flex items-center gap-2 text-sm'>
              <Switch
                checked={pattern.active}
                disabled={readOnly || toggle.isPending}
                onCheckedChange={on => toggle.mutate(on)}
              />
              {pattern.active ? 'Active' : 'Inactive'}
            </label>
          )}
          {pattern && canManage && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='text-destructive'
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete the pattern "${pattern.name}"?`)) remove.mutate()
              }}
            >
              <Trash2 className='mr-1.5 h-3.5 w-3.5' /> Delete
            </Button>
          )}
        </div>
        {canManage && (
          <div className='flex items-center gap-3'>
            {unpicked >= 0 && (
              <span className='text-xs text-amber-700'>Pick a shift for {cycleDayLabel(unpicked)}</span>
            )}
            <Button
              type='button'
              size='sm'
              disabled={!dirty || !draft.name.trim() || unpicked >= 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
              {pattern ? 'Save changes' : 'Create pattern'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

/** Which components the cycle uses, in their colours, with how many days each. */
function ComponentLegend({
  days,
  shiftById,
  toneOf,
}: {
  days: DayDraft[]
  shiftById: Map<string, ShiftDefinition>
  toneOf: (id: string) => (typeof SHIFT_TONES)[number]
}) {
  const counts = new Map<string, number>()
  for (const d of days) if (d.isOn && d.shiftId) counts.set(d.shiftId, (counts.get(d.shiftId) ?? 0) + 1)
  if (counts.size === 0) return null
  return (
    <div className='flex flex-wrap items-center gap-2'>
      {[...counts].map(([id, n]) => {
        const sh = shiftById.get(id)
        if (!sh) return null
        return (
          <span key={id} className='inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-0.5 text-xs'>
            <span className={cn('h-2.5 w-2.5 rounded-full', toneOf(id).chip)} />
            <span className='font-mono font-semibold'>{sh.code}</span>
            <span className='text-muted-foreground'>
              {sh.startTime}–{sh.endTime} · {n} {n === 1 ? 'day' : 'days'}
            </span>
          </span>
        )
      })}
    </div>
  )
}

/**
 * Component — the shift worked on one cycle day. Every active component is
 * listed; one not worked on every weekday this day can fall on is shown but
 * cannot be chosen, with the reason. "Every week" sets the same weekday in
 * each week of the cycle at once.
 */
function ComponentDialog({
  dayIndex,
  cycleDays,
  current,
  shifts,
  toneOf,
  onClose,
  onChoose,
}: {
  dayIndex: number
  cycleDays: number
  current: DayDraft
  shifts: ShiftDefinition[]
  toneOf: (id: string) => (typeof SHIFT_TONES)[number]
  onClose: () => void
  /** null = day off. */
  onChoose: (shiftId: string | null, everyWeek: boolean) => void
}) {
  const [everyWeek, setEveryWeek] = useState(false)
  const weeks = Math.ceil(cycleDays / 7)
  const weekday = WEEKDAY_LETTERS[dayIndex % 7]
  const drifts = cycleDays % 7 !== 0
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-lg' onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader className='space-y-1 border-b px-6 pb-4 pt-6 text-left'>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <CalendarDays className='h-4 w-4' />
            </span>
            Component · {cycleDayLabel(dayIndex)}
          </DialogTitle>
          <DialogDescription>
            Choose the component worked on this day.
            {drifts && ` A ${cycleDays}-day cycle drifts across the week, so only components worked every day fit.`}
          </DialogDescription>
        </DialogHeader>

        <div className='max-h-[55vh] space-y-1.5 overflow-y-auto px-6 py-4'>
          {shifts.length === 0 && (
            <p className='rounded-xl border border-dashed px-3 py-6 text-center text-sm text-muted-foreground'>
              No active components — add one in Shift definitions.
            </p>
          )}
          {shifts.map(sh => {
            const fits = shiftFitsDay(sh.weekdays, dayIndex, cycleDays)
            const chosen = current.isOn && current.shiftId === sh.id
            return (
              <button
                key={sh.id}
                type='button'
                disabled={!fits}
                onClick={() => onChoose(sh.id, everyWeek)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all',
                  chosen && 'border-primary bg-primary/[0.05] ring-2 ring-primary/20',
                  fits && !chosen && 'hover:border-foreground/25 hover:bg-muted/40',
                  !fits && 'cursor-not-allowed opacity-50',
                )}
              >
                <span
                  className={cn(
                    'flex h-10 min-w-[3rem] items-center justify-center rounded-lg px-1.5 font-mono text-xs font-bold text-white',
                    toneOf(sh.id).chip,
                  )}
                >
                  {sh.code}
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-sm font-medium'>{sh.name}</span>
                  <span className='block text-xs text-muted-foreground tabular-nums'>
                    {sh.startTime}–{sh.endTime} · {formatHours(sh.hours)} net · {weekdaysLabel(sh.weekdays)}
                  </span>
                  {!fits && (
                    <span className='block text-xs text-amber-700'>
                      {drifts ? 'Not worked every day' : `Not worked on ${weekday}`}
                    </span>
                  )}
                </span>
                {chosen && <Check className='h-4 w-4 shrink-0 text-primary' />}
              </button>
            )
          })}
          <button
            type='button'
            onClick={() => onChoose(null, everyWeek)}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border border-dashed px-3 py-2.5 text-left transition-all hover:bg-muted/40',
              !current.isOn && 'border-primary bg-primary/[0.05]',
            )}
          >
            <span className='flex h-10 min-w-[3rem] items-center justify-center rounded-lg bg-muted text-xs font-semibold text-muted-foreground'>
              OFF
            </span>
            <span className='flex-1 text-sm font-medium'>Day off</span>
            {!current.isOn && <Check className='h-4 w-4 shrink-0 text-primary' />}
          </button>
        </div>

        <DialogFooter className='items-center gap-3 border-t bg-muted/30 px-6 py-3 sm:justify-between'>
          {weeks > 1 && !drifts ? (
            <label className='flex cursor-pointer items-center gap-2 text-sm'>
              <Switch checked={everyWeek} onCheckedChange={setEveryWeek} />
              Every {weekday} in the cycle
            </label>
          ) : (
            <span />
          )}
          <Button type='button' variant='ghost' onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** One cycle day: its component's code and hours in that component's colour, or OFF. */
function DayCell({
  index,
  day,
  shift,
  tone,
  misfit,
  readOnly,
  onChoose,
}: {
  index: number
  day: DayDraft
  shift: ShiftDefinition | undefined
  tone: string | undefined
  misfit: boolean
  readOnly: boolean
  onChoose: () => void
}) {
  const label = cycleDayLabel(index)
  const title = misfit
    ? shift
      ? `${shift.code} is not worked on every day this day can fall on — choose another component`
      : 'Choose a component for this day'
    : shift
      ? `${label}: ${shift.name} · ${shift.startTime}–${shift.endTime} · ${formatHours(shift.hours)} net`
      : `${label}: off`
  return (
    <button
      type='button'
      disabled={readOnly}
      aria-label={`${label}: ${day.isOn ? (shift?.code ?? 'no component') : 'off'}`}
      title={title}
      onClick={onChoose}
      className={cn(
        'flex h-12 w-full flex-col items-center justify-center rounded-lg border text-xs transition-all',
        day.isOn && shift && !misfit && tone,
        misfit && 'border-2 border-dashed border-amber-500 bg-amber-50 text-amber-800',
        !day.isOn && 'border-dashed bg-background text-muted-foreground',
        !readOnly && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md',
        readOnly && 'cursor-default',
      )}
    >
      {day.isOn ? (
        <>
          <span className='font-mono text-[13px] font-bold leading-none'>{shift?.code ?? '?'}</span>
          {shift && <span className='mt-1 text-[10px] leading-none opacity-80'>{formatHours(shift.hours)}</span>}
        </>
      ) : (
        <span className='text-[11px] font-medium'>OFF</span>
      )}
    </button>
  )
}
